import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import type { PendingAttributeValue } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type PendingValuesListProps = {
    error: unknown;
    items: PendingAttributeValue[] | null;
    onPromote: (typeId: string, valueId: string) => void;
    onRetry: () => void;
};

/**
 * Pending-review queue. Values here are created by product import (not built
 * yet), so the list is empty today — the empty state is the common case.
 */
export const PendingValuesList: React.FC<PendingValuesListProps> = ({
    error,
    items,
    onPromote,
    onRetry,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            empty: t('attributes.components.dictionary.empty.pending'),
            promote: t('attributes.components.dictionary.actions.promote'),
            errorMessage: t('attributes.components.dictionary.pendingError'),
            retry: t('attributes.components.dictionary.errorRetry'),
        }),
        [t],
    );

    return (
        <Widget
            className="flex flex-col gap-0 overflow-hidden p-0"
            {...getNodeTestIdProp('attribute-pending-widget')}
        >
            {error ? (
                <div
                    className="flex flex-col items-center gap-3 py-12 text-muted"
                    {...getNodeTestIdProp('attribute-pending-error')}
                >
                    <Icon
                        aria-hidden
                        className="size-8 text-danger"
                        icon="gravity-ui:circle-exclamation"
                    />
                    <span className="text-sm">{copy.errorMessage}</span>
                    <Button onPress={onRetry} size="sm" variant="outline">
                        {copy.retry}
                    </Button>
                </div>
            ) : items === null ? (
                <div className="flex flex-col gap-3 p-4">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <Skeleton
                            className="h-12 w-full rounded-lg"
                            key={`attribute-pending-loading-${index}`}
                        />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div
                    className="flex flex-col items-center gap-3 py-12 text-muted"
                    {...getNodeTestIdProp('attribute-pending-empty')}
                >
                    <Icon
                        aria-hidden
                        className="size-8 text-default-500"
                        icon="gravity-ui:circle-check"
                    />
                    <span
                        className="text-sm"
                        {...getNodeTestIdProp('attribute-pending-empty-title')}
                    >
                        {copy.empty}
                    </span>
                </div>
            ) : (
                <ul {...getNodeTestIdProp('attribute-pending-list')}>
                    {items.map((item) => (
                        <li
                            className="flex items-center justify-between gap-3 border-b border-separator px-4 py-3 last:border-b-0"
                            key={item.value.id}
                            {...getNodeTestIdProp(
                                `attribute-pending-item-${item.value.id}`,
                            )}
                        >
                            <div className="flex min-w-0 flex-col">
                                <span className="text-sm font-medium text-foreground">
                                    {item.value.name}
                                </span>
                                <span className="font-mono text-xs text-muted">
                                    {item.attributeTypeName} · {item.value.slug}
                                </span>
                            </div>
                            <Button
                                onPress={() =>
                                    onPromote(
                                        item.attributeTypeId,
                                        item.value.id,
                                    )
                                }
                                size="sm"
                                variant="secondary"
                                {...getNodeTestIdProp(
                                    `attribute-pending-promote-${item.value.id}`,
                                )}
                            >
                                {copy.promote}
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </Widget>
    );
};
